import { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, BookOpen, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const LedgerCtx = createContext(null);

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Global, reusable ledger drill-down. Wrap the app once with
 * <LedgerDrilldownProvider> and call `openLedger(accountCodeOrName, { name, startDate, endDate })`
 * from anywhere (via the `useLedgerDrilldown` hook) to pop a modal listing the
 * source journal-entry transactions behind any balance. Backed by
 * GET /accounting/general-ledger?account_code= (matches code OR name).
 */
export const LedgerDrilldownProvider = ({ children }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [acct, setAcct] = useState('');           // code or name used to query
  const [acctName, setAcctName] = useState('');
  const [branchId, setBranchId] = useState(null); // iter823 — mirror caller's branch filter
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async (code, sd, ed, pg = 1, bId = null) => {
    setLoading(true);
    try {
      const params = { account_code: code, start_date: sd, end_date: ed, page: pg };
      if (bId) params.branch_id = bId;
      const { data } = await api.get('/accounting/general-ledger', { params });
      setData(data);
      setPage(pg);
    } catch {
      setData({ entries: [], total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  const openLedger = useCallback((codeOrName, opts = {}) => {
    const sd = opts.startDate || monthStart();
    const ed = opts.endDate || today();
    const bId = opts.branchId || null;
    setAcct(codeOrName);
    setAcctName(opts.name || codeOrName);
    setBranchId(bId);
    setStart(sd);
    setEnd(ed);
    setData(null);
    setOpen(true);
    load(codeOrName, sd, ed, 1, bId);
  }, [load]);

  const entries = data?.entries || [];
  const pages = data?.pages || 0;

  return (
    <LedgerCtx.Provider value={{ openLedger }}>
      {children}
      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-3xl" data-testid="ledger-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-teal-600" />
              {data?.account?.code ? `${data.account.code} — ` : ''}{data?.account?.name || acctName}
            </DialogTitle>
            <DialogDescription>Source transactions behind this balance.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">From</label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-36" data-testid="ledger-start-date" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">To</label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-36" data-testid="ledger-end-date" />
            </div>
            <Button size="sm" className="h-9" onClick={() => load(acct, start, end, 1, branchId)} data-testid="ledger-apply-btn">Apply</Button>
            <Button size="sm" variant="outline" className="h-9 ml-auto" data-testid="ledger-full-view-btn"
              onClick={() => { setOpen(false); navigate(`/accounting/general-ledger?account=${encodeURIComponent(acct)}&start_date=${start}&end_date=${end}${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ''}`); }}>
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Full ledger
            </Button>
          </div>

          <div className="max-h-[55dvh] overflow-auto rounded-lg border border-border mt-1">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
            ) : entries.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground" data-testid="ledger-entries-empty">No transactions in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ledger-entries-table">
                  <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground sticky top-0">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Date</th>
                      <th className="text-left font-medium px-3 py-2">Entry #</th>
                      <th className="text-left font-medium px-3 py-2">Description</th>
                      <th className="text-right font-medium px-3 py-2">Debit</th>
                      <th className="text-right font-medium px-3 py-2">Credit</th>
                      <th className="text-right font-medium px-3 py-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr key={i} className="border-t border-border hover:bg-muted/30 cursor-pointer" data-testid={`ledger-entry-row-${i}`}
                        onClick={() => { if (e.entry_number) { setOpen(false); navigate(`/journal-entries?entry_number=${encodeURIComponent(e.entry_number)}`); } }}>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{(e.date || '').slice(0, 10)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{e.entry_number || '—'}</td>
                        <td className="px-3 py-2">{e.description}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-blue-600">{e.debit ? fmt(e.debit) : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-600">{e.credit ? fmt(e.credit) : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(e.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {pages > 1 && (
            <div className="flex justify-center gap-1 flex-wrap">
              {Array.from({ length: pages }, (_, i) => (
                <Button key={i} size="sm" variant={i + 1 === page ? 'default' : 'outline'} className="h-7 w-7 p-0"
                  onClick={() => load(acct, start, end, i + 1, branchId)}>{i + 1}</Button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </LedgerCtx.Provider>
  );
};

export const useLedgerDrilldown = () => {
  const ctx = useContext(LedgerCtx);
  return ctx || { openLedger: () => {} };
};

/** Convenience: a clickable element that opens the drill-down for `account`. */
export const LedgerLink = ({ account, name, startDate, endDate, className = '', children, ...rest }) => {
  const { openLedger } = useLedgerDrilldown();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openLedger(account, { name, startDate, endDate }); }}
      className={`hover:underline decoration-dotted underline-offset-2 cursor-pointer ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};
